/**
 * Normalising and checksum-validating a Swiss UID, with an optional VAT suffix.
 * Mirrors the backend's `UidNormalizer` (eCH-0097 Modulo-11) for instant feedback before the rate-limited register lookup;
 * the backend re-validates. `normalize()` works on the bare 12-character UID, while `format()` produces the stored punctuated form.
 */

export type VatSuffix = 'MWST' | 'TVA' | 'IVA';

const VAT_SUFFIXES: readonly VatSuffix[] = ['MWST', 'TVA', 'IVA'];

/** `CHE` + 9 digits, plus an optional trailing suffix (validated against VAT_SUFFIXES separately). */
const PATTERN = /^CHE(\d{8})(\d)([A-Z]*)$/;

/** Weights applied to the eight number digits, in order, before the check digit. */
const CHECK_DIGIT_WEIGHTS = [5, 4, 3, 2, 7, 6, 5, 4];

/** Strips everything but letters and digits, and uppercases what remains. Does not validate. */
export function normalizeUid(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
}

/** Whether the normalized value is a well-formed, checksum-valid UID with an optional recognised suffix. */
export function isValidUid(normalized: string): boolean {
  return parse(normalized) !== null;
}

/** The recognised VAT suffix on a valid value, or null when there is none. */
export function suffixOf(normalized: string): VatSuffix | null {
  return parse(normalized)?.suffix ?? null;
}

/** The bare 12-character UID, dropping any suffix. Assumes an already-valid value. */
export function bareUid(normalized: string): string {
  return normalized.slice(0, 12);
}

/** the canonical punctuated storage form, or null for an invalid value (this runs on live input) */
export function formatUid(normalized: string): string | null {
  const parsed = parse(normalized);
  if (!parsed) return null;

  const { nineDigits, suffix } = parsed;
  const formatted = `CHE-${nineDigits.slice(0, 3)}.${nineDigits.slice(3, 6)}.${nineDigits.slice(6, 9)}`;

  return suffix === null ? formatted : `${formatted} ${suffix}`;
}

function parse(normalized: string): { nineDigits: string; suffix: VatSuffix | null } | null {
  const match = PATTERN.exec(normalized);
  if (!match) return null;

  const [, eightDigits, checkDigitChar, suffixText] = match;
  if (checkDigitFor(eightDigits) !== Number(checkDigitChar)) return null;

  let suffix: VatSuffix | null = null;
  if (suffixText !== '') {
    if (!VAT_SUFFIXES.includes(suffixText as VatSuffix)) return null;
    suffix = suffixText as VatSuffix;
  }

  return { nineDigits: eightDigits + checkDigitChar, suffix };
}

/** The eCH-0097 check digit for the given eight number digits, or null when none is valid. */
function checkDigitFor(eightDigits: string): number | null {
  let sum = 0;
  for (let position = 0; position < CHECK_DIGIT_WEIGHTS.length; position++) {
    sum += Number(eightDigits[position]) * CHECK_DIGIT_WEIGHTS[position];
  }

  const checkDigit = 11 - (sum % 11);
  if (checkDigit === 11) return 0;
  if (checkDigit === 10) return null;
  return checkDigit;
}
